import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PrivateLayout from "../../layouts/PrivateLayout";
import useLanguage from "../../utils/useLanguage";
import useTokenAndProfile from "../../components/hooks/FetchUserProfile";
import { useUserProfile } from "../../context/UserProfileContext";
import { fetchRaidSignups, addRaidSignup, updateRaidSignup, fetchUserCharacters } from "../../api/api";
import { WOW_CLASSES, CLASS_COLORS, ROLES, getSpecRole } from "../../data/wowClasses";
import "@fortawesome/fontawesome-free/css/all.min.css";

const t = {
  en: {
    title: "Raid Season Sign-Up",
    subtitle: "Pick your team and add your character to the roster.",
    team: "Team",
    team1: "Team 'HoF'",
    team2: "Team 'CE'",
    name: "Your Name",
    namePlaceholder: "e.g. John / Discord handle",
    character: "Character",
    noCharacters: "No characters found on your Battle.net account.",
    loadingCharacters: "Loading characters...",
    spec: "Spec",
    submit: "Sign Up",
    submitting: "Signing up...",
    success: "You're signed up!",
    loadError: "Failed to load sign-ups.",
    submitError: "Failed to sign up. Please try again.",
    nameRequired: "Please enter your name.",
    characterRequired: "Please select a character.",
    loading: "Loading roster...",
    empty: "No one has signed up for this team yet.",
    columnPlayer: "Player",
    columnCharacter: "Character",
    columnClass: "Class",
    columnSpec: "Spec",
    columnFlex: "Flexible",
    roleTank: "Tank",
    roleHealer: "Healer",
    roleMelee: "Melee DPS",
    roleRanged: "Ranged DPS",
    roleDps: "DPS",
    flexLabel: "Flexible to also play",
    rosterTitle: "Current Roster",
    refresh: "Refresh",
    edit: "Edit",
    save: "Save Changes",
    saving: "Saving...",
    updateSuccess: "Your sign-up was updated!",
    updateError: "Failed to update sign-up. Please try again.",
    editingNotice: "You're already signed up — editing your entry below",
    removeNotice: "If you want your entry removed, contact an officer.",
    youBadge: "You",
    errorNotYourEntry: "This sign-up doesn't belong to you.",
    errorEntryNotFound: "This entry no longer exists. The roster has been refreshed.",
    errorInvalidCombo: "That class/spec combination isn't valid.",
  },
  bg: {
    title: "Записване за Сезон 2",
    subtitle: "Избери отбор и добави своя герой в състава.",
    team: "Отбор",
    team1: "Отбор 'HoF'",
    team2: "Отбор 'CE'",
    name: "Твоето Име",
    namePlaceholder: "напр. Impresko",
    character: "Герой",
    noCharacters: "Няма намерени герои в твоя Battle.net акаунт.",
    loadingCharacters: "Зареждане на герои...",
    spec: "Специализация",
    submit: "Запиши се",
    submitting: "Записване...",
    success: "Записан си успешно!",
    loadError: "Неуспешно зареждане на записванията.",
    submitError: "Неуспешно записване. Опитай отново.",
    nameRequired: "Моля, въведи името си.",
    characterRequired: "Моля, избери герой.",
    loading: "Зареждане на състава...",
    empty: "Все още никой не се е записал за този отбор.",
    columnPlayer: "Играч",
    columnCharacter: "Герой",
    columnClass: "Клас",
    columnSpec: "Спек",
    columnFlex: "Роли",
    roleTank: "Танк",
    roleHealer: "Хийлър",
    roleMelee: "Melee DPS",
    roleRanged: "Ranged DPS",
    roleDps: "DPS",
    flexLabel: "Мога да играя и като",
    rosterTitle: "Текущ Състав",
    refresh: "Обнови",
    edit: "Редактирай",
    save: "Запази промените",
    saving: "Запазване...",
    updateSuccess: "Записването е обновено!",
    updateError: "Неуспешна промяна на записването.",
    editingNotice: "Вече си записан — редактираш своя запис по-долу",
    removeNotice: "Ако искаш записът ти да бъде премахнат, свържи се с офицер.",
    youBadge: "Ти",
    errorNotYourEntry: "Този запис не е твой.",
    errorEntryNotFound: "Този запис вече не съществува. Съставът беше обновен.",
    errorInvalidCombo: "Тази комбинация клас/специализация не е валидна.",
  },
};

const FLEX_ROLES = [
  { key: "tank", labelKey: "roleTank", icon: "fa-shield-halved", color: "text-blue-400", accent: "accent-blue-400" },
  { key: "healer", labelKey: "roleHealer", icon: "fa-heart-pulse", color: "text-green-400", accent: "accent-green-400" },
  { key: "dps", labelKey: "roleDps", icon: "fa-burst", color: "text-red-400", accent: "accent-red-400" },
];

const ROLE_META = [
  { role: ROLES.TANK, labelKey: "roleTank", icon: "fa-shield-halved", color: "text-blue-400" },
  { role: ROLES.HEALER, labelKey: "roleHealer", icon: "fa-heart-pulse", color: "text-green-400" },
  { role: ROLES.MELEE, labelKey: "roleMelee", icon: "fa-hand-fist", color: "text-red-400" },
  { role: ROLES.RANGED, labelKey: "roleRanged", icon: "fa-crosshairs", color: "text-purple-400" },
];

const getCharacterId = (character) => String(character.characterId || character.id || "");

const RoleCounters = ({ entries, strings }) => {
  const counts = entries.reduce((acc, entry) => {
    const role = getSpecRole(entry.className, entry.spec);
    if (role) acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-4 gap-2 mb-3">
      {ROLE_META.map(({ role, labelKey, icon, color }) => (
        <div
          key={role}
          className="flex flex-col items-center justify-center bg-gray-900/70 border border-white/10 rounded-lg py-2.5"
        >
          <i className={`fas ${icon} ${color} text-lg mb-1`}></i>
          <span className="text-white font-bold text-sm">{counts[role] || 0}</span>
          <span className="text-gray-400 text-[10px] uppercase tracking-wide text-center leading-tight">
            {strings[labelKey]}
          </span>
        </div>
      ))}
    </div>
  );
};

const ROSTER_GRID_COLS = "grid-cols-[1.1fr_1.3fr_0.9fr_0.9fr_0.8fr]";

const RosterTable = ({ title, entries, strings, currentUserId }) => (
  <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
    <div className="bg-white/90 px-4 py-3">
      <h3 className="text-sm font-bold uppercase tracking-widest text-gray-700">
        {title}
      </h3>
    </div>
    <div
      className={`grid ${ROSTER_GRID_COLS} gap-x-3 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-gray-500 border-b border-gray-200`}
    >
      <div>{strings.columnPlayer}</div>
      <div>{strings.columnCharacter}</div>
      <div>{strings.columnClass}</div>
      <div>{strings.columnSpec}</div>
      <div>{strings.columnFlex}</div>
    </div>
    <div className="divide-y divide-white/5 bg-gray-900/60">
      {entries.length === 0 ? (
        <div className="px-4 py-6 text-center text-gray-400 text-sm">
          {strings.empty}
        </div>
      ) : (
        entries.map((entry) => {
          const isOwn = Boolean(currentUserId) && entry.userId === currentUserId;
          return (
            <div
              key={entry.id}
              className={`grid ${ROSTER_GRID_COLS} gap-x-3 items-center px-4 py-3 text-sm ${
                isOwn ? "bg-emerald-500/10" : ""
              }`}
            >
              <div className="min-w-0 truncate text-white font-medium" title={entry.name}>
                {entry.name}
                {isOwn && (
                  <span className="ml-1.5 text-[10px] font-semibold text-emerald-400 uppercase align-middle">
                    {strings.youBadge}
                  </span>
                )}
              </div>
              <div
                className="min-w-0 truncate text-gray-200"
                title={entry.realm ? `${entry.characterName} — ${entry.realm}` : entry.characterName}
              >
                {entry.characterName}
              </div>
              <div
                className="min-w-0 truncate font-medium"
                style={{ color: CLASS_COLORS[entry.className] || "#D1D5DB" }}
                title={entry.className}
              >
                {entry.className}
              </div>
              <div className="min-w-0 truncate text-gray-300" title={entry.spec}>
                {entry.spec}
              </div>
              <div className="flex items-center gap-1.5">
                {FLEX_ROLES.map(({ key, icon, color, labelKey }) => (
                  <i
                    key={key}
                    className={`fas ${icon} text-xs ${
                      entry[`flex${key.charAt(0).toUpperCase()}${key.slice(1)}`]
                        ? color
                        : "text-gray-700"
                    }`}
                    title={strings[labelKey]}
                  ></i>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
);

function RaidSignups() {
  const { lang, toggleLang } = useLanguage();
  const strings = t[lang];
  const { token } = useTokenAndProfile();
  const { userProfile } = useUserProfile();
  const currentUserId = userProfile?.id || userProfile?.userId || null;
  const prefilledEntryIdRef = useRef(null);

  const [team1, setTeam1] = useState([]);
  const [team2, setTeam2] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [myCharacters, setMyCharacters] = useState([]);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(false);

  const [team, setTeam] = useState("team1");
  const [playerName, setPlayerName] = useState("");
  const [selectedCharacterId, setSelectedCharacterId] = useState("");
  const [className, setClassName] = useState("");
  const [spec, setSpec] = useState("");
  const [flexRoles, setFlexRoles] = useState({ tank: false, healer: false, dps: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = await fetchRaidSignups(token);
      setTeam1(data?.team1 || []);
      setTeam2(data?.team2 || []);
    } catch (error) {
      console.error("Error fetching raid sign-ups:", error);
      setErrorMessage(error.message || strings.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [token, strings.loadError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyCharacterSelection = (character) => {
    setSelectedCharacterId(character ? getCharacterId(character) : "");
    const newClassName = character?.playable_class?.name || "";
    setClassName(newClassName);
    const specs = WOW_CLASSES.find((c) => c.name === newClassName)?.specs || [];
    setSpec(specs[0]?.name || "");
  };

  useEffect(() => {
    if (!token) return;
    setIsLoadingCharacters(true);
    fetchUserCharacters(token)
      .then((data) => {
        const characters = Array.isArray(data) ? data : data?.characters || data?.data || [];
        setMyCharacters(characters);
        if (characters.length > 0) {
          applyCharacterSelection(characters[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching characters:", error);
      })
      .finally(() => setIsLoadingCharacters(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCharacterChange = (charId) => {
    const character = myCharacters.find((c) => getCharacterId(c) === charId);
    applyCharacterSelection(character);
  };

  const toggleFlexRole = (key) => {
    setFlexRoles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const clearForm = () => {
    setEditingId(null);
    setPlayerName("");
    setTeam("team1");
    setFlexRoles({ tank: false, healer: false, dps: false });
    if (myCharacters.length > 0) {
      applyCharacterSelection(myCharacters[0]);
    } else {
      setSelectedCharacterId("");
      setClassName("");
      setSpec("");
    }
  };

  const applyEntryToForm = (entry) => {
    setEditingId(entry.id);
    setTeam(entry.team || "team1");
    setPlayerName(entry.name || "");
    setFlexRoles({
      tank: Boolean(entry.flexTank),
      healer: Boolean(entry.flexHealer),
      dps: Boolean(entry.flexDps),
    });

    const matchedCharacter = myCharacters.find(
      (c) =>
        c.name === entry.characterName &&
        (c.realm?.name || c.realm || "") === (entry.realm || "")
    );
    setSelectedCharacterId(matchedCharacter ? getCharacterId(matchedCharacter) : "");
    setClassName(entry.className || "");
    setSpec(entry.spec || "");
  };

  const ownEntry =
    [...team1, ...team2].find(
      (entry) => Boolean(currentUserId) && entry.userId === currentUserId
    ) || null;

  // Pre-fill the form with the user's existing sign-up (once per entry) so the
  // form edits it instead of creating a duplicate. Waits for characters to finish
  // loading so the character dropdown can be matched correctly.
  useEffect(() => {
    if (!ownEntry || isLoadingCharacters) return;
    if (prefilledEntryIdRef.current === ownEntry.id) return;
    applyEntryToForm(ownEntry);
    prefilledEntryIdRef.current = ownEntry.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownEntry, isLoadingCharacters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      toast.error(strings.nameRequired);
      return;
    }
    const character = myCharacters.find((c) => getCharacterId(c) === selectedCharacterId);
    if (!character) {
      toast.error(strings.characterRequired);
      return;
    }

    const payload = {
      team,
      name: playerName.trim(),
      characterName: character.name,
      realm: character.realm?.name || character.realm || "",
      className,
      spec,
      flexTank: flexRoles.tank,
      flexHealer: flexRoles.healer,
      flexDps: flexRoles.dps,
    };

    setIsSubmitting(true);
    try {
      let savedEntry;
      if (editingId) {
        savedEntry = await updateRaidSignup(token, editingId, payload);
        setTeam1((prev) => prev.filter((e) => e.id !== editingId));
        setTeam2((prev) => prev.filter((e) => e.id !== editingId));
        toast.success(strings.updateSuccess);
      } else {
        savedEntry = await addRaidSignup(token, payload);
        toast.success(strings.success);
      }
      if (team === "team1") {
        setTeam1((prev) => [...prev, savedEntry]);
      } else {
        setTeam2((prev) => [...prev, savedEntry]);
      }
      applyEntryToForm(savedEntry);
      prefilledEntryIdRef.current = savedEntry.id;
    } catch (error) {
      console.error("Error saving raid sign-up:", error);
      if (editingId) {
        if (error.status === 403) {
          toast.error(strings.errorNotYourEntry);
          clearForm();
          prefilledEntryIdRef.current = null;
          loadData();
        } else if (error.status === 404) {
          toast.error(strings.errorEntryNotFound);
          clearForm();
          prefilledEntryIdRef.current = null;
          loadData();
        } else if (error.status === 400) {
          toast.error(error.message || strings.errorInvalidCombo);
        } else {
          toast.error(error.message || strings.updateError);
        }
      } else {
        toast.error(error.message || strings.submitError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500";

  return (
    <PrivateLayout>
      <div className="min-h-screen flex flex-col">
        <div
          className="fixed inset-0 -z-10"
          style={{
            backgroundImage: "url('/wow-wallpaper.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="fixed inset-0 bg-black/70 -z-10 pointer-events-none" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={toggleLang}
                className="flex items-center space-x-1.5 text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200"
                title={lang === "en" ? "Switch to Bulgarian" : "Смени на Английски"}
              >
                <span>{lang === "en" ? "🇧🇬 БГ" : "🇬🇧 EN"}</span>
              </button>
            </div>
            <div className="text-center mb-10">
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-serif tracking-wide mb-3">
                {strings.title}
              </h1>
              <p className="text-gray-300 text-base sm:text-lg">
                {strings.subtitle}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className={`bg-gray-900/70 border rounded-xl p-5 sm:p-6 mb-10 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end ${
                editingId ? "border-emerald-500/50" : "border-gray-700"
              }`}
            >
              {editingId && (
                <div className="sm:col-span-4 flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <i className="fas fa-pen"></i>
                  <span>{strings.editingNotice}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
                  {strings.team}
                </label>
                <select
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  className={inputClass}
                >
                  <option value="team1" className="bg-gray-900">
                    {strings.team1}
                  </option>
                  <option value="team2" className="bg-gray-900">
                    {strings.team2}
                  </option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
                  {strings.name}
                </label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder={strings.namePlaceholder}
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
                  {strings.character}
                </label>
                {isLoadingCharacters ? (
                  <p className="text-gray-400 text-sm py-2">{strings.loadingCharacters}</p>
                ) : myCharacters.length === 0 ? (
                  <p className="text-gray-400 text-sm py-2">{strings.noCharacters}</p>
                ) : (
                  <select
                    value={selectedCharacterId}
                    onChange={(e) => handleCharacterChange(e.target.value)}
                    className={inputClass}
                  >
                    {myCharacters.map((c) => {
                      const id = getCharacterId(c);
                      const realmName = c.realm?.name || c.realm || "";
                      const label = [c.name, realmName].filter(Boolean).join(" — ");
                      return (
                        <option key={id} value={id} className="bg-gray-900">
                          {label}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
                  {strings.spec}
                </label>
                <select
                  value={spec}
                  onChange={(e) => setSpec(e.target.value)}
                  disabled={!className}
                  className={`${inputClass} disabled:opacity-50`}
                >
                  {(WOW_CLASSES.find((c) => c.name === className)?.specs || []).map((s) => (
                    <option key={s.name} value={s.name} className="bg-gray-900">
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-4">
                <span className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
                  {strings.flexLabel}
                </span>
                <div className="flex flex-wrap items-center gap-4">
                  {FLEX_ROLES.map(({ key, labelKey, icon, color, accent }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={flexRoles[key]}
                        onChange={() => toggleFlexRole(key)}
                        className={`w-4 h-4 rounded ${accent}`}
                      />
                      <i className={`fas ${icon} ${color}`}></i>
                      <span>{strings[labelKey]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-4 flex flex-col sm:flex-row sm:items-center gap-3">
                {editingId && (
                  <p className="text-xs text-gray-400 sm:mr-auto">
                    <i className="fas fa-circle-info mr-1.5"></i>
                    {strings.removeNotice}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting || myCharacters.length === 0}
                  className="w-full sm:w-auto sm:ml-auto bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 shadow-md"
                >
                  {editingId
                    ? isSubmitting
                      ? strings.saving
                      : strings.save
                    : isSubmitting
                    ? strings.submitting
                    : strings.submit}
                </button>
              </div>
            </form>

            {errorMessage && (
              <div className="mb-6 bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
                {errorMessage}
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{strings.rosterTitle}</h2>
              <button
                type="button"
                onClick={loadData}
                disabled={isLoading}
                className="flex items-center gap-2 text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
              >
                <i className={`fas fa-arrows-rotate ${isLoading ? "animate-spin" : ""}`}></i>
                <span>{strings.refresh}</span>
              </button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-300 text-sm">{strings.loading}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <RoleCounters entries={team1} strings={strings} />
                  <RosterTable
                    title={strings.team1}
                    entries={team1}
                    strings={strings}
                    currentUserId={currentUserId}
                  />
                </div>
                <div>
                  <RoleCounters entries={team2} strings={strings} />
                  <RosterTable
                    title={strings.team2}
                    entries={team2}
                    strings={strings}
                    currentUserId={currentUserId}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      </div>
    </PrivateLayout>
  );
}

export default RaidSignups;
